/**
 * The few lines that touch the real `localStorage`.
 *
 * Everything interesting about the home screen's pre-fill is in
 * `src/lib/last-expense.ts`, which takes the storage as an argument and so can
 * be tested without a browser. This is the other end of that: the one module
 * in the pair that names a global, kept to exactly the one it needs so that
 * the tested part stays the whole part. `src/theme-document.ts` and
 * `src/motion-document.ts` are its twins.
 */

import {
  readLastExpense,
  rememberLastExpense,
  type LastExpense,
} from './lib/last-expense'

/** `localStorage`, or nothing — Safari in private mode throws on the property. */
function safeStorage() {
  try {
    return window.localStorage
  } catch {
    return null
  }
}

/** What this browser filed last, as far as it can be believed. */
export function lastExpenseFiled(): LastExpense | null {
  return readLastExpense(safeStorage())
}

/**
 * Called when an expense is actually filed, from wherever it was filed —
 * `AddExpense` is the one sheet, so this is one call in one place. Correcting
 * an expense does not come through here: an edit is somebody fixing a row from
 * March, and letting it move "what you file most days" would make the pre-fill
 * worse for every screen after it.
 */
export function rememberExpenseFiled(last: LastExpense): void {
  rememberLastExpense(last, safeStorage())
}
